import type {MithraMaterializedApi} from "../../../services/MithraMaterializedApi";
import type {BagStore} from "../../../stores/BagStore";
import {makeAutoObservable, reaction} from "mobx";
import {RequestManager} from "../../../stores/managers/RequestManager";
import {forkJoin, from} from "rxjs";
import type {
    MatCategoryConcentrationStatistics,
    MatConcentrationStatistics,
    MatSupplierCategoryConcentrationStatistics
} from "../../../services/classes/MaterializedClasses";
import type {AxiosResponse} from "axios";
import type {BarDataPoint} from "../../../components/visualization/BarChart";
import {toCurrency} from "../../../components/currency-component/CurrencyClasses";
import {UNCATEGORIZED_LABEL, UNCATEGORIZED_VALUE} from "../../../constants";
import type ProfileStore from "../../../stores/ProfileStore";
import type {MatSearch, MatSupplierFilter_V2} from "../../../services/classes/MatReviewClasses";
import {catsToDict} from "../../../services/ApiHelpers";
import type {PageResponse} from "../../../services/ApiTypes.ts";
import {SCPageController} from "./controllers/SCPageController.ts";
import type AuthStore from "../../../authentication/AuthStore.tsx";

export class SpendConcentrationStore {
    readonly sCPageController: SCPageController;

    sCPage = 0;
    setSCPage = (value: number) => {
        this.sCPage = value;
        this.spendConcentrationSupplierRM.request(this.supplierRequestFilter);
    }
    sCPerPage = 25;
    setSCPerPage = (value: number) => {
        this.sCPerPage = value;
        this.setSCPage(0);
    }

    spendConcentrationSupplierRM =
        new RequestManager<MatSupplierFilter_V2, AxiosResponse<PageResponse<MatSupplierCategoryConcentrationStatistics>>>(
            filter => from(this.api.listPaginatedSpendConcentrationSupplier(filter, this.sCPage + 1, this.sCPerPage)));

    _spendConcentrationRequestManager = new RequestManager<{databag: number, taxonomySize: number},
        [
                MatConcentrationStatistics | null,
            AxiosResponse<MatCategoryConcentrationStatistics[]>,
        ]>(
        ({databag, taxonomySize}) => forkJoin([
            from(this.api.listSpendConcentration(databag)),
            from(this.api.listSpendConcentrationCategory(databag, taxonomySize)),
        ] as const)
    )

    // noinspection JSUnusedLocalSymbols
    constructor(
        private auth: AuthStore,
        private api: MithraMaterializedApi,
        private bagStore: BagStore,
        private profileStore: ProfileStore,
    ) {
        this.sCPageController = new SCPageController(api, auth, bagStore);
        makeAutoObservable(this);
        reaction(() => ([this.bagStore.bagId]), ([databagId]) => {
            this._spendConcentrationRequestManager.request({
                databag: databagId,
                taxonomySize: this.bagStore.taxonomy_size
            });
            if (this.supplierRequestFilter) {
                this.spendConcentrationSupplierRM.request(this.supplierRequestFilter);
            } else {
                this.spendConcentrationSupplierRM.cleanup();
            }
        });
        reaction(() => [this.sCPageController.filterController.supplierSearchValue], ([_v]) => {
            // This will also trigger a request
            this.setSCPage(0);
        });
    }

    /**
     * When something is selected, filter on one level deeper
     */
    get supplierRequestFilter(): MatSupplierFilter_V2 {
        const filterLevel = this.sCPageController.filterController.selectedLevel + (this.sCPageController.filterController.canSelectLevelDeeper ? 1 : 0);
        const fixLevels = this.sCPageController.filterController.selectedLevel;
        const selectedCats = catsToDict(this.sCPageController.filterController.selectedCategory, fixLevels);
        let search: MatSearch | undefined = undefined;
        if (this.sCPageController.filterController.supplierSearchValue) {
            search = {
                supplier: this.sCPageController.filterController.supplierSearchValue,
            }
        }
        return {
            databag: this.bagStore.bagId,
            // business_unit: this.bagStore.bu.selectedBusinessUnitId,
            business_unit: undefined,
            search,
            filterLevel,
            fixLevels,
            ...selectedCats,
        }
    }

    get groupedData(): BarDataPoint[] | undefined {
        if (!this._spendConcentrationRequestManager.result) return undefined;
        const currencySymbol = this.profileStore.currencySymbol;
        const level = this.sCPageController.filterController.selectedLevel + (this.sCPageController.filterController.canSelectLevelDeeper ? 1 : 0);
        return this._spendConcentrationRequestManager.result[1].data
            .filter(d => {
                if (d.level !== level) return false;
                // Check if the parent levels match the current selection
                for (let i = 0; i < level - 1; i++) {
                    const category = d[`l${i + 1}`] as string;
                    if (this.sCPageController.filterController.selectedCategory[i] !== category) {
                        return false;
                    }
                }
                return true;
            })
            .map<BarDataPoint>(d => {
                const category = d[`l${level}`] as string;
                const categoryLabel = category === UNCATEGORIZED_VALUE ? UNCATEGORIZED_LABEL : category;
                const valueLabel = `${Math.round(d.top_n_spend_percentage * 100) + '%'}`
                    + `  |  ${toCurrency(currencySymbol, d.total_spend)}`
                    + `  |  ${d.distinct_suppliers} suppliers`;
                return {value: d.top_n_spend_percentage, valueLabel, category, categoryLabel};
            })
    }

    get topValue(): number | undefined {
        if (!this._spendConcentrationRequestManager.result) return undefined;
        return this._spendConcentrationRequestManager.result[0]?.top_n_spend_percentage || undefined;
    }

    get topValueP(): number | undefined {
        if (this.topValue) return this.topValue * 100;
        return undefined;
    }

    get otherValue(): number | undefined {
        if (!this._spendConcentrationRequestManager.result) return undefined;
        const topNSpendPercentage = this._spendConcentrationRequestManager.result[0]?.top_n_spend_percentage;
        if (!topNSpendPercentage) return undefined;
        return 1 - topNSpendPercentage;
    }

    get echartData(): Array<Array<any>> {
        if (this.spendConcentrationSupplierRM.busy) return [];
        return (this.spendConcentrationSupplierRM.result?.data.results ?? []).map(item => ([
            item.s_name, item.s_c_spend, item.supplier_id,
        ]));
    }
}
